
/**
 * Billing Page - Data Table Implementation
 * Refactored for high data density and better information hierarchy.
 */

import { useState, useMemo } from 'react'
import {
  Plus,
  RefreshCw,
  FileSpreadsheet,
} from 'lucide-react'
import { Button } from '@/components/ui/button'

import { useInvoices, useUpdateInvoice } from '../hooks/useBilling'
import { useTenants } from '@/features/tenants/hooks/useTenants'
import type { InvoiceStatus } from '@/lib/types'
import { CreateInvoiceModal } from './CreateInvoiceModal'
import { GenerateInvoicesModal } from './GenerateInvoicesModal'
import { BillingStats } from './BillingStats'
import { BillingFilters } from './BillingFilters'
import { BillingList } from './BillingList'
import type { FilterStatus } from '../types'

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================
export function BillingPage() {
  const { data: allInvoices = [], isLoading, refetch: refetchInvoices } = useInvoices()
  const { data: tenantsResponse, refetch: refetchTenants } = useTenants()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tenants = Array.isArray(tenantsResponse) ? tenantsResponse : ((tenantsResponse as any)?.data || [])
  const updateStatusMutation = useUpdateInvoice()

  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showGenerateModal, setShowGenerateModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  // Stats
  const stats = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const invoices = Array.isArray(allInvoices) ? allInvoices : []
    return {
      totalRevenue: invoices
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .filter((i: any) => i.status === 'paid')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .reduce((sum: number, i: any) => sum + i.amountCents, 0),
      pendingAmount: invoices
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .filter((i: any) => i.status === 'sent')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .reduce((sum: number, i: any) => sum + i.amountCents, 0),
      overdueAmount: invoices
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .filter((i: any) => i.status === 'overdue')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .reduce((sum: number, i: any) => sum + i.amountCents, 0),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      paidCount: invoices.filter((i: any) => i.status === 'paid').length,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      pendingCount: invoices.filter((i: any) => i.status === 'sent').length,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      overdueCount: invoices.filter((i: any) => i.status === 'overdue').length,
    }
  }, [allInvoices])

  // Filtering
  const filteredInvoices = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return allInvoices.filter((invoice: any) => {
      const matchesSearch = searchQuery === '' ||
        invoice.tenant?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        invoice.month?.includes(searchQuery) ||
        `#${invoice.id}`.includes(searchQuery)

      const matchesStatus = filterStatus === 'all' || invoice.status === filterStatus

      return matchesSearch && matchesStatus
    })
  }, [allInvoices, searchQuery, filterStatus])

  // Pagination
  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage)
  const paginatedInvoices = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return filteredInvoices.slice(startIndex, endIndex)
  }, [filteredInvoices, currentPage, itemsPerPage])

  // Reset to page 1 when filters change
  useMemo(() => {
    setCurrentPage(1)
  }, [searchQuery, filterStatus])

  const handleUpdateStatus = (id: number, status: InvoiceStatus) => {
    updateStatusMutation.mutate({ id, status })
  }

  const handleRefresh = () => {
    refetchInvoices()
    refetchTenants()
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Billing</h2>
          <p className="text-sm text-muted-foreground">Manage invoices and revenue tracking.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} className="h-9">
            <RefreshCw className={`h-3.5 w-3.5 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowGenerateModal(true)} className="h-9">
            <FileSpreadsheet className="h-3.5 w-3.5 mr-2" />
            Generate Monthly
          </Button>
          <Button onClick={() => setShowCreateModal(true)} className="h-9">
            <Plus className="h-4 w-4 mr-2" />
            Create Invoice
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <BillingStats stats={stats} />

      {/* Filters Bar */}
      <BillingFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
      />

      {/* Data Table */}
      <BillingList
        invoices={paginatedInvoices}
        isLoading={isLoading}
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalCount={filteredInvoices.length}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={setItemsPerPage}
        onUpdateStatus={handleUpdateStatus}
      />

      <CreateInvoiceModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        tenants={tenants}
      />

      <GenerateInvoicesModal
        isOpen={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
      />
    </div>
  )
}
